import { DecimalPipe } from '@angular/common';
import { Component, OnInit, Input, OnChanges } from '@angular/core';
import { DeviceService } from 'src/app/home/site-dashboard/device/device.service';
import { CommonService } from '../services/common.service';
import { DeviceFactory } from 'src/app/home/site-dashboard/device/device-factory/device-factory';
import { mainDeviceConfig } from 'src/app/home/site-dashboard/device/device-factory/device-config';

@Component({
	selector: 'app-info-box',
	templateUrl: './info-box.component.html',
	styleUrls: ['./info-box.component.css']
})
export class InfoBoxComponent implements OnInit, OnChanges {
	@Input() isForCustomer?: boolean = false;
	@Input() chargLinkDevices?: any;
	@Input() ioTAhDevices?: any;
	@Input() battLinkDevices?: any;
	@Input() iotemeterDevices?: any;
	@Input() hours: number;
	@Input() ahrs: number;
	@Input() whrs: any;
	@Input() billedWhrs: number;
	@Input() deviceTypeFilterSelected: string[] = [];

	chargLinkDevicesUnits = {
		ahrs: "",
		whrs: "",
		billed_whrs: "",
		hours: ""
	}
	ioTAhDevicesUnits = {
		ahrs: "",
		whrs: "",
		billed_whrs: "",
		hours: ""
	}
	battLinkDevicesUnits = {
		battery_inuse_ahrs: "",
		battery_charge_ahrs: "",
		billed_whrs: ""
	}

	iotemeterDevicesUnits = {
		total_consumed_energy: "",
		current_year_consumed_energy: "",
	}

	private readonly nonWhrsUnits: string[] = ['', '', 'Mil', 'Bil', 'Tril', 'P', 'E', 'Z', 'Y'];
	private readonly whrsUnits: string[] = ['', 'k', 'M', 'G', 'T', 'P', 'E', 'Z', 'Y'];
	private readonly metricDecimals = 1;

	CO2: string;
	numberOfDevicesIcon: string;

	constructor(
		private commonService: CommonService,
		private deviceService: DeviceService,
	) { }

	ngOnInit() {
	}

	ngOnChanges() {
		let decimalPipe: DecimalPipe = new DecimalPipe("en-US");
		if (this.ioTAhDevices)
			this.format(decimalPipe);
		if (this.chargLinkDevices)
			this.format(decimalPipe, DeviceFactory.types.charglink);
		if (this.battLinkDevices)
			this.formatBattlinkInfo(decimalPipe, DeviceFactory.types.battlink);

		if (this.iotemeterDevices)
			this.formatIotemeterInfo();
	}

	format(decimalPipe: DecimalPipe, deviceType = DeviceFactory.types.iotah) {
		const deviceConfig: mainDeviceConfig = DeviceFactory.createDevice(deviceType).config;
		const devicesOptions = {
			[DeviceFactory.types.iotah]: 'ioTAhDevices',
			[DeviceFactory.types.charglink]: 'chargLinkDevices'
		}
		const key = devicesOptions[deviceType];

		const { billedWhrs, billedWhrsUnit } = this.formatBilledWhrs(decimalPipe, this[key].billed_whrs);
		this[key].billed_whrs = billedWhrs;
		this[`${key}Units`].billed_whrs = billedWhrsUnit;

		const ahrsValue = Math.round(this[key].ahrs / 3600);
		const { value: ahrs, unit: ahrsUnit } = this.formatNonWhrsMetric(ahrsValue);
		this[key].ahrs = ahrs;
		this[`${key}Units`].ahrs = ahrsUnit;

		const { whrs, whrsUnit } = this.formatWhrs(this[key].whrs, 1);
		this[key].whrs = whrs;
		this[`${key}Units`].whrs = whrsUnit;

		if (deviceConfig.CO2data)
			this.formatCO2data(decimalPipe);

		const hoursValue = Math.round(this[key].hours / 3600);
		const { value: formattedHours, unit: hoursUnit } = this.formatNonWhrsMetric(hoursValue);

		this[key].hours = formattedHours;
		this[`${key}Units`].hours = hoursUnit;
	}

	formatBilledWhrs(decimalPipe: DecimalPipe, billedWhrsData) {
		let billedWhrs = billedWhrsData / 3600;
		let billedWhrsObj: any = { result: decimalPipe.transform(Math.round(billedWhrs) / 1000, '1.0-0') };
		billedWhrs = billedWhrsObj.result;
		const billedWhrsUnit = billedWhrsObj.unit ? billedWhrsObj.unit : '';
		return { billedWhrs, billedWhrsUnit }
	}

	formatCO2data(decimalPipe: DecimalPipe) {
		let co2 = this.deviceService.calculateCO2(this.billedWhrs / 1000);
		this.CO2 = decimalPipe.transform(co2, '1.0-2');
	}

	formatAhrs(ahrsData) {
		let ahrsObj = this.commonService.formatTotalDeviceNumbers(Math.round(ahrsData / 3600));
		const ahrs = ahrsObj.result;
		const ahrsUnit = ahrsObj.unit ? ahrsObj.unit : '';
		return { ahrs, ahrsUnit }
	}

	scaleMetric(value: number, units: string[]) {
		if (!Number.isFinite(value)) {
			return { value: '0', unit: '' };
		}

		const abs = Math.abs(value);
		let step = 0;

		while (step + 1 < units.length && abs >= Math.pow(1000, step + 1))
			step++;

		while (step > 0 && !units[step])
			step--;

		if (!step) {
			return { value: value.toLocaleString(), unit: '' };
		}

		return {
			value: (value / Math.pow(1000, step)).toFixed(this.metricDecimals),
			unit: units[step]
		};
	}

	formatNonWhrsMetric(value: number) {
		return this.scaleMetric(value, this.nonWhrsUnits);
	}

	formatWhrs(whrsData: number, multiplyFactor = 1000) {
		if (whrsData == null || isNaN(whrsData)) {
			return { whrs: '0', whrsUnit: '' };
		}

		const { value, unit } = this.scaleMetric(whrsData * multiplyFactor, this.whrsUnits);

		return { whrs: value, whrsUnit: unit };
	}

	showIoTahInfo(): boolean {
		return (this.deviceTypeFilterSelected.length === 0 ||
			this.deviceTypeFilterSelected.includes(this.commonService.DEVICES_TYPE.Truklink) ||
			this.deviceTypeFilterSelected.includes(this.commonService.DEVICES_TYPE.Truklink_ps))
			&& this.ioTAhDevices?.count > 0;
	}

	showChargLinkInfo(): boolean {
		return (this.deviceTypeFilterSelected.length === 0 ||
			this.deviceTypeFilterSelected.includes(this.commonService.DEVICES_TYPE.ChargLink))
			&& this.chargLinkDevices?.count > 0;
	}

	formatBattlinkInfo(decimalPipe: DecimalPipe, deviceType = DeviceFactory.types.battlink) {
		if (deviceType != DeviceFactory.types.battlink &&
			deviceType != DeviceFactory.types.lithiumBattlink &&
			deviceType != DeviceFactory.types.battlinkII)
			return;

		const deviceConfig: mainDeviceConfig = DeviceFactory.createDevice(deviceType).config;
		const key = 'battLinkDevices';

		if (this[key].battery_inuse_ahrs != null) {
			const valueInUse = Math.round(this[key].battery_inuse_ahrs / 3600);
			const { value, unit } = this.formatNonWhrsMetric(valueInUse);

			this[key].battery_inuse_ahrs = value;
			this[`${key}Units`].battery_inuse_ahrs = unit;
		}

		if (this[key].battery_charge_ahrs != null) {
			const valueCharge = Math.round(this[key].battery_charge_ahrs / 3600);
			const { value, unit } = this.formatNonWhrsMetric(valueCharge);

			this[key].battery_charge_ahrs = value;
			this[`${key}Units`].battery_charge_ahrs = unit;
		}

		if (deviceConfig.CO2data)
			this.formatCO2data(decimalPipe);
	}

	showBattLinkInfo(): boolean {
		return (this.deviceTypeFilterSelected.length === 0 ||
			this.deviceTypeFilterSelected.includes(this.commonService.DEVICES_TYPE.Battlink) ||
			this.deviceTypeFilterSelected.includes(this.commonService.DEVICES_TYPE.LithiumBattlink) ||
			this.deviceTypeFilterSelected.includes(this.commonService.DEVICES_TYPE.BattlinkII))
			&& this.battLinkDevices?.count > 0
	}

	formatIotemeterInfo() {
		const key = 'iotemeterDevices';

		const { whrs, whrsUnit } = this.formatWhrs(this[key].total_consumed_energy);
		this[key].total_consumed_energy = whrs;
		this[`${key}Units`].total_consumed_energy = whrsUnit;

		const { whrs: currentYearWhrs, whrsUnit: currentYearWhrsUnit } = this.formatWhrs(this[key].current_year_consumed_energy);
		this[key].current_year_consumed_energy = currentYearWhrs;
		this[`${key}Units`].current_year_consumed_energy = currentYearWhrsUnit;
	}

	showIotemeterInfo(): boolean {
		return this.iotemeterDevices &&
			this.iotemeterDevices?.count && (
				!this.deviceTypeFilterSelected.length ||
				this.deviceTypeFilterSelected.includes(this.commonService.DEVICES_TYPE.ioTEmeter) ||
				this.deviceTypeFilterSelected.includes(this.commonService.DEVICES_TYPE.ioTEmeterEdge) ||
				this.deviceTypeFilterSelected.includes(this.commonService.DEVICES_TYPE.ioTEmeterSp1) ||
				this.deviceTypeFilterSelected.includes(this.commonService.DEVICES_TYPE.ioTEmeterSp3)
			);

	}
}
